/**
 * Numeric limits used by branded schemas and validators.
 *
 * These are the single source of truth for the sim's integer ranges. Any
 * WP that needs a bound imports it from here — never inline literals.
 *
 * Rationale for the specific numbers is captured beside each constant.
 */

import { SCHEMA_HASH } from "./generated/schemaHash.generated.js";

/**
 * Maximum coordinate on either axis. `pit-design-spec` §5.1 says arena
 * ≈ 4000×4000; wp1 says 4096 = 2^12 for a clean power-of-two ceiling.
 */
export const MAX_UNITS = 4096;
export const MIN_UNITS = -MAX_UNITS;

/**
 * Sim runs at 30 tps → 30 min match ≈ 54000 ticks. 2^24 = 16.7M ticks is
 * ~155 h; more than enough headroom without approaching MAX_SAFE_INTEGER.
 */
export const MAX_TICK = 1 << 24;

/** Speed cap: 256 units/tick ≈ 7.7 m/s at world scale. Generous. */
export const MAX_UNITS_PER_TICK = 256;
export const MIN_UNITS_PER_TICK = 0;

/**
 * Champion mass, for walk-push and weighted knockbacks (#28). Unitless
 * relative scale — only weight RATIOS matter (defender share of a push is
 * `idiv(step * pusherWeight, pusherWeight + defenderWeight)`). Minimum 1
 * so a weight sum is always ≥ 2 and the idiv denominator is never zero.
 * Cap 100000 mirrors HP's ceiling; more than enough dynamic range between
 * a feather and an anchor while keeping `step * weight` well inside safe
 * integer bounds (step ≤ MAX_UNITS_PER_TICK = 256).
 */
export const MAX_WEIGHT = 100000;
export const MIN_WEIGHT = 1;

/** HP cap: 100000. MVP champion has ~1000 HP; leaves room for bosses. */
export const MAX_HP = 100000;
export const MIN_HP = 0;

/** Max seats/teams. MVP 1v1; keep 8 for future 4v4 without another bump. */
export const MAX_SEAT = 15;
export const MAX_TEAM = 15;

/** Ability slot bar: WoW-like 12 slots is plenty for MVP + growth. */
export const MAX_ABILITY_SLOT = 31;

/**
 * #323: max abilities in a player's pre-match LOADOUT — "choose 12 of N from
 * your champion's ability pool." Deliberately independent of
 * `MAX_ABILITY_SLOT` (which bounds how big a champion's full content pool can
 * ever get, up to 32): a loadout is capped at 12 even for a future champion
 * whose pool grows past that. As of 2026-07-18 no shipped champion's pool
 * exceeds this (Priest is the largest at 11), so today every valid loadout is
 * simply "all of the champion's abilities" — the cap only starts constraining
 * real choices once a champion's pool grows past it.
 */
export const MAX_LOADOUT_SIZE = 12;

/**
 * Sequence numbers reset per WebSocket connection. A 10-minute connection
 * at 30 tps with 4 inputs/tick averages 72k; 2^30 gives ~5 years' headroom
 * without approaching MAX_SAFE_INTEGER.
 */
export const MAX_SEQ = 1 << 30;

/** FNV-1a 32-bit unsigned range. */
export const MAX_STATE_HASH = 0xffffffff;

/**
 * Max valid `Facing` sector index. Wire schema enforces `0..MAX_FACING`.
 *
 * MUST stay in sync with `FACING_SECTORS` in `packages/sim/src/types.ts`.
 * At the current setting (16-way facing) this is 15. When facing widens
 * (e.g. to 32 sectors), bump BOTH constants together and bump
 * `SIM_VERSION` — the wire format is a behaviour change.
 */
export const MAX_FACING = 15;

/** Identifier length caps. */
export const MAX_ID_LEN = 64;

/**
 * Maximum size of a raw client message *before* JSON parse. WP5 enforces
 * this at the socket layer; WP1 exports the constant as the single source
 * of truth. 4096 bytes is comfortably above the largest legal message
 * (a full input carrying an entity-id target) and well below any WebSocket
 * frame limit.
 */
export const MAX_MESSAGE_BYTES = 4096;

/**
 * #833: hard cap on one in-game chat message's length, in UTF-16 code units
 * (what `z.string().max()` counts). 160 sits in the 100–200 band the issue
 * specified and is the familiar SMS ceiling — long enough for a real callout
 * ("incoming from the left pillar, save your interrupt for the cast after
 * this one") and short enough that the floating text it renders as stays
 * readable above a unit's head for one lifetime.
 *
 * TWO reasons this is a schema-level cap rather than a client-side courtesy:
 *   1. GRIEFING. Chat is the first CLIENT-AUTHORED string this protocol ever
 *      BROADCASTS to other players. An uncapped one is a trivially abusable
 *      screen-filler.
 *   2. SNAPSHOT SIZE. It rides on `WIRE_SIM_STATE_SCHEMA` (see
 *      `WIRE_CHAT_MESSAGE_SCHEMA`), which the server sends to every seat at
 *      the full tick rate. `MAX_CHAT_TEXT_LEN * MAX_CHAT_PER_SNAPSHOT` is the
 *      worst case a single snapshot's chat array can add, and both halves are
 *      pinned here so that bound is auditable in one place.
 *
 * Note this is a CHARACTER cap, not a byte cap — 160 astral-plane emoji are
 * ~640 UTF-8 bytes. `MAX_MESSAGE_BYTES` (4096) is the byte-level backstop on
 * the inbound client message, and it is comfortably above that worst case.
 */
export const MAX_CHAT_TEXT_LEN = 160;

/**
 * #833: hard cap on how many chat messages ONE snapshot may carry. The room
 * drains its pending-chat queue into the next snapshot and drops anything
 * beyond this, so the wire array can never exceed it (the schema's `.max()`
 * is the loud self-check on that invariant). 8 is generous for a 6-seat room
 * at 30 tps — a seat would have to send 40 messages/second to reach it — and
 * bounds the per-snapshot chat payload at 8 × 160 chars.
 */
export const MAX_CHAT_PER_SNAPSHOT = 8;

/**
 * ISSUE-856: bounds on a player's `profile.displayName` — the nickname other
 * players see. Pinned HERE, in the protocol package, because exactly three
 * things must agree on them and two of them are not the server's validator:
 * the wire schema (`WIRE_ROSTER_ENTRY_SCHEMA`), the HTTP rename endpoint's
 * validator (`apps/server/src/nickname.ts`), and the curated guest-name
 * generator (`apps/server/src/guestNames.ts`, whose every possible output is
 * asserted to satisfy the same validator). A name that cannot ride the wire
 * must never be storable.
 *
 * 3 is the floor because a 1–2 character name is indistinguishable from a
 * placeholder and unsearchable; 20 is the ceiling because the name renders
 * above a unit's head in a 6-seat room (same readability argument as
 * `MAX_CHAT_TEXT_LEN`, with far less room to spend).
 */
export const MIN_DISPLAY_NAME_LEN = 3;
export const MAX_DISPLAY_NAME_LEN = 20;

/**
 * ISSUE-856: `profile.displayTag` — the Discord-style numeric discriminator
 * that makes the display identity the `(name, tag)` PAIR, so two players can
 * both be `Shadow` without either land-grabbing the bare name. Exactly four
 * digits, zero-padded (`"0427"`), which is why it is a string and not a
 * number: `0427` and `427` must not be the same tag.
 */
export const DISPLAY_TAG_PATTERN = /^[0-9]{4}$/u;

/** ISSUE-856: how many distinct tags exist under ONE display name — the
 *  denominator of the tag allocator's collision math (`profileService.ts`). */
export const DISPLAY_TAG_SPACE = 10000;

/** ContentHash regex: `"sha256:<64 lower-hex chars>"`. */
export const CONTENT_HASH_PATTERN = /^sha256:[0-9a-f]{64}$/u;

/**
 * Content-id nanoid parameters (#163). The unique part of a `<prefix>_<nanoid>`
 * content id is a `nanoid` of length `NANOID_LENGTH` over the base62 alphabet
 * `NANOID_ALPHABET`. Base62 (NO `_`/`-`) is deliberate: it keeps the SINGLE `_`
 * (the prefix separator) unambiguous, so `typeOf`/the id patterns split on the
 * first `_` with zero ambiguity. Pinned HERE as the one source of truth — the
 * `content:new` authoring tool mints against these, and the `*_ID_PATTERN`s
 * below encode them for the loader/wire boundary. 62^10 ≈ 8.4×10^17 keeps a
 * hand-authored set collision-free by construction (#41's dup check is the loud
 * backstop regardless).
 */
export const NANOID_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
export const NANOID_LENGTH = 10;

/**
 * Content-id patterns (#163) — `<3-lowercase-letter prefix>_<10-char base62>`.
 * Mirrors `CONTENT_HASH_PATTERN`: one canonical regex per minted content class,
 * enforced by the branded id schema at the loader/wire boundary (fail loud on a
 * malformed id). Total length is always 14 (`aur_`/`chm_`/`map_` = 4, + 10).
 * The base62 random part (see `NANOID_ALPHABET`) excludes `_`/`-`, so the id
 * carries exactly one `_` (the separator).
 *
 *   aur_ → aura def   (a reference target: applyAura.def, engine constants)
 *   chm_ → champion   (a reference target: on the wire, team-select)
 *   map_ → map        (a reference target: mode/match config)
 */
export const AURA_ID_PATTERN = /^aur_[0-9A-Za-z]{10}$/u;
export const CHAMPION_ID_PATTERN = /^chm_[0-9A-Za-z]{10}$/u;
export const MAP_ID_PATTERN = /^map_[0-9A-Za-z]{10}$/u;
/** #190: ability def id — `abl_<10-char base62>`. Builds on #163's reserved
 *  `abl_` prefix; abilities are now a first-class reference target (a champion's
 *  `abilities` array is a list of these ids). */
export const ABILITY_ID_PATTERN = /^abl_[0-9A-Za-z]{10}$/u;

/** Full length of a minted content id: 3-letter prefix + `_` + 10-char nanoid. */
export const CONTENT_ID_LEN = 14;

/** Sim version regex: semver-ish `X.Y.Z` for stamping into replays. */
export const SIM_VERSION_PATTERN = /^\d+\.\d+\.\d+$/u;

/** Protocol version constant embedded in `hello` / `welcome` / `snapshot`.
 *  v2: added `quickJoin` client message + QueueSpec (matchmaking).
 *  v3: added required `championId` to createMatch / joinMatch / quickJoin
 *      (per-player champion selection, #11).
 *  v4: added required `weight` to champion stats (mass-based walk-push, #28).
 *  v5: added optional `delivery` field to abilities (instant | projectile) +
 *      projectiles in snapshot state (travel-time + body-blocking, #29).
 *  v6: added per-entity `auras` array to the snapshot state (#30 aura
 *      system reaches the wire). Render-relevant instance fields only;
 *      effect payloads / stacking rules stay server-side in content.
 *  v7: added the cosmetic combat-event stream (#48) to the wire.
 *  v8: added optional per-ability `facingPolicy` + the `Tuning`
 *      `facingArcCosThresholdBp` default to the content schema (#27 per-ability
 *      facing gates + opt-in auto-snap). Additive — existing content parses
 *      unchanged; mismatched clients get the existing `protocolMismatch`
 *      close.
 *  v9: added optional per-ability `minRangeUnits` (min-range/deadzone, #125),
 *      optional champion-stat `armor` (physical mitigation, #121), and the
 *      `Tuning` `armorMaxReductionBp` default (#121) to the content schema.
 *      Additive — existing content parses unchanged (absent ⇒ no deadzone /
 *      0 armor / 7500bp cap).
 *  v10: BATCH stealth (#91) + points (#90) content-schema additions — all
 *      additive/optional, so existing content parses unchanged:
 *        • #91: optional `breakOnDealDamage` flag on an aura def (deal-damage
 *          break twin of `breakOnDamageThreshold`); the stealth aura is authored
 *          as content, keyed by `STEALTH_AURA_DEF_ID`.
 *        • #90: optional `points` pool on a champion; two new effect kinds
 *          (`buildPoints`, `spendPoints`); optional `perPointBonus` on `damage`
 *          and `durationPerPoint` on `applyAura` (scale-by-points hooks).
 *  v11: BATCH cone area (#128) + positional arc (#132) content-schema changes:
 *        • #128: `area` is now a discriminated `shape` union — CIRCLE (the
 *          default; a shape-less area still parses as a circle, so existing
 *          content is unchanged) or CONE (`arcCosThresholdBp`, optional
 *          `minRangeUnits`, `maxRangeUnits`, `origin`). Replaces the earlier
 *          `cone: { lengthUnits, halfAngleTanBp }` sub-object.
 *        • #132: optional `arcCosThresholdBp` on `positionalPolicy` (omit ⇒ the
 *          new 180/180 hemisphere default; `flank` requires it). Additive.
 *  v12: BATCH shadowstep (#134) + dispel (#135) content-schema changes — all
 *      additive/optional, so existing content parses unchanged:
 *        • #134: a fourth `displace` direction `behindTarget` (self-relocation
 *          flush behind the target); `displace.distance` is now OPTIONAL at the
 *          schema level (crossValidate requires it for the three target-movers,
 *          rejects it for `behindTarget`).
 *        • #135: optional `ignoreUndispellable` on the `dispel` effect + optional
 *          `undispellable` flag on an aura def (Mass-Dispel override + hard-
 *          defensive gate). The `dispel` effect itself is now wired in the sim.
 *  v13: dynamic terrain / walls (#129) — a new `spawnWall` effect kind (a
 *      spawnable, temporary obstacle collision + LoS honor: Ice Wall /
 *      Anivia-style, plus one-way / Wind-Wall / smoke via blocking FLAGS).
 *      Additive: a new member of the `EFFECT_SCHEMA` discriminated union +
 *      the `WALL_BLOCKED_TEAMS_SCHEMA` enum, so existing content parses
 *      unchanged.
 *  v14: auto-attack system (#93) — additive/optional content-schema changes,
 *      so existing content parses unchanged:
 *        • an optional `weapon` block on a champion (`WEAPON_SCHEMA`:
 *          swingTicks / damage / range / delivery instant|projectile /
 *          optional projectile / schools / requireFacing / optional
 *          minRangeUnits). The sim desugars it into an auto-cast weapon
 *          ability on a parallel off-GCD swing track.
 *        • an optional `allowAutoWhileCasting` flag on an ability (default
 *          false ⇒ the auto is suppressed for that ability's whole cast).
 *  v15: projectile momentum (#149) — an optional `momentumBp` scalar on the
 *      projectile DELIVERY (`DELIVERY_PROJECTILE_SCHEMA`) and on the ranged
 *      auto-attack weapon's projectile block (`WEAPON_PROJECTILE_SCHEMA`).
 *      Additive/optional (absent ⇒ 0 ⇒ today's behavior), so existing content
 *      parses unchanged. Lets a projectile inherit the caster's movement
 *      velocity (direction + speed) at spawn; range stays fixed.
 *  v16: BATCH forced-movement (#147) + cast-while-moving (#154). Additive/
 *      optional content-schema changes, so existing content parses unchanged:
 *        • a new `forcedMovement` AURA primitive (`AURA_PRIM_FORCED_MOVEMENT_
 *          SCHEMA`: mode away|toward|fixedPath + optional looping `path`),
 *          appended to the aura-primitive discriminated union. Drives sustained
 *          fear/charm/disorient movement through the shared movement path.
 *        • an optional `castWhileMoving` flag on an ability
 *          (`ABILITY_SCHEMA`; default false ⇒ casting roots you, as before).
 *      Neither adds hashed ENTITY state; forced movement's per-INSTANCE cursor
 *      rides `AuraInstance` (sim-side, append-only).
 *  v17: BATCH wall flexibility (#146 items 1/3/4/5) + entity blocksLoS (#158).
 *      Additive/optional content-schema changes, so existing content parses
 *      unchanged:
 *        • the `spawnWall` effect gains `losBlockedTeams` (LoS team gate, default
 *          `all` = symmetric), `blockDirection` (Wind-Wall directional projectile
 *          block, default `both`), optional `geometry` (a `polyline` of authored
 *          vertices vs the default `straight` row-of-circles), and optional
 *          `hazard` (fire/thorn/freeze on-contact effect payload, ground-zone
 *          cadence).
 *        • a new `blocksLoS` AURA primitive (`AURA_PRIM_BLOCKS_LOS_SCHEMA`:
 *          `teams` all|enemies|allies, default `enemies`), appended to the
 *          aura-primitive discriminated union — an entity body that team-blocks
 *          line-of-sight ("hide behind the big guy").
 *      No new hashed ENTITY state (blocksLoS is a def-authored inert flag); the
 *      wall additions ride the already-hashed `walls` array (append-only).
 *  v18: BATCH CC classification (#142). A closed 7-class CC taxonomy + two
 *      structural fixes:
 *        • #142 D1: `CC_CLASS_SCHEMA` (closed enum: stun/mez/slow/fear/silence/
 *          disarm/root) + a first-class `ccClasses` field on the aura def
 *          (default `[]`, so existing auras parse unchanged). Rollup selectors
 *          (`CC_ROLLUPS`: hardCC = stun+mez+fear, movementImpair = slow+root).
 *        • #142 D2 (BREAKING): the standalone `slow` EFFECT is REMOVED from the
 *          effect union. Slows migrate to ordinary aura defs (ccClasses:["slow"]
 *          + a moveSpeed `statMod`) applied via `applyAura`. Content that used
 *          `{ kind: "slow" }` no longer parses (this is the only non-additive
 *          change — hence the version bump is mandatory, not just courtesy).
 *        • #142 D3: a new `IMMUNITY_SCOPE` value `forcedMove` (an independent
 *          axis, NOT a CC class) that blocks the `displace` effect + the
 *          `forcedMovement` aura drive. Additive to the immunity scope union.
 *  v19: forcedMovement mode rename (#157, follow-up from #155 review). The
 *      `forcedMovement` aura primitive's mode literals `away`/`toward` (shipped
 *      at v16) are renamed to `awayFromSource`/`towardSource` to match the
 *      `displace` effect's direction names (same schema file) and the locked
 *      #147 D2 decision. `fixedPath` is unchanged. Cosmetic string-literal
 *      rename with NO shipped content using `forcedMovement` yet, so the CONTENT
 *      hash is UNCHANGED and no golden/replay shifts — but it IS a content-schema
 *      shape change (old `away`/`toward` content no longer parses), hence the
 *      PROTOCOL_VERSION bump. NOT observable sim behavior on any shipped/golden
 *      content (the sim switch relabels its cases identically), so SIM_VERSION
 *      is NOT bumped.
 *  v20: re-cast / toggle abilities (#183) — an optional `recast` block on
 *      `ABILITY_SCHEMA` (`RECAST_SCHEMA`: optional `windowTicks`). Additive/
 *      optional (absent ⇒ an ordinary one-shot ability), so existing content
 *      parses unchanged and the CONTENT hash is unchanged for non-users. Opts an
 *      ability into the press-again second-stage subsystem: a second press of the
 *      same slot, while the stage-1 entity (v1: a spawned ground zone) is alive /
 *      within the recast window, routes as a free off-GCD RECAST that ends the
 *      stage-1 entity instead of starting a fresh cast.
 *  v21: content identity model — opaque `<prefix>_<nanoid>` ids + `slug` (#163,
 *      executes the #163 design §8). The three content-def id schemas are
 *      TIGHTENED to a fixed `<prefix>_<10-char base62>` pattern (14 chars):
 *      `AURA_DEF_ID_SCHEMA` (aur_), `CHAMPION_ID_SCHEMA` (chm_), and a new
 *      `MAP_ID_SCHEMA` (map_) — so `AURA_DEF_SCHEMA`/`CHAMPION_SCHEMA`/`MAP_SCHEMA`
 *      (and the wire `championId` reference) reject a malformed id at the
 *      loader/wire boundary. Each of the three def schemas also gains a REQUIRED
 *      `slug` (`z.string().min(1).max(64)`, no default — a real authoring
 *      decision, rename-safe, soft-unique per set enforced in tooling/CI, NOT the
 *      boot loader). Ids are minted once at authoring time via the `content:new`
 *      tool (base62 nanoid, len 10), committed + immutable — NEVER at load. The
 *      shipped set (2 champions, 1 map, 4 auras) is re-minted big-bang; the two
 *      engine-owned aura constants (STUN_/STEALTH_AURA_DEF_ID) carry committed
 *      minted VALUES. Content-schema reshape (tightened id pattern + new `slug`
 *      field) ⇒ PROTOCOL_VERSION 20→21. Reserved (table only, no id field yet):
 *      abl_/eff_/tag_/itm_/mod_.
 *  v21: BATCH onDispelled (#187) + stopCast (#188) + Demonic Circle recast
 *      (#186). Additive content/wire-schema changes, so existing content + wire
 *      messages parse unchanged (non-users are byte-identical):
 *        • #188 (stopCast): a new `stopCast` SimInput kind (`SIM_INPUT_STOP_CAST_
 *          SCHEMA`) appended to the `SIM_INPUT_SCHEMA` discriminated union — a
 *          player-initiated cast cancel, distinct from `stop` (movement).
 *        • #187 (onDispelled): a new `onDispelled` value on `TRIGGER_EVENT_SCHEMA`
 *          — a `trigger` fired when a `dispel` removes the holding aura (the
 *          reacting effect targets the dispeller). Additive enum value.
 *        • #186 (circle recast): `RECAST_SCHEMA` gains a `targetKind`
 *          (`zone`|`circle`, default `zone` ⇒ existing recast content parses
 *          byte-identically). `circle` opts an instant self-cast into storing a
 *          recall point at the caster's feet + a range-capped blink-recall on the
 *          second press (crossValidate pins instant delivery + a required
 *          `windowTicks`). No new hashed ENTITY state on shipped content.
 *  v22: abilities become a standalone content registry (#190). `ABILITY_SCHEMA`
 *      gains a REQUIRED `id` (`abl_<nanoid>`, a new `ABILITY_ID_SCHEMA`/`AbilityId`
 *      brand building on #163's reserved `abl_` prefix) + `slug`, and LOSES its
 *      `slot` field (slot is now the ability's INDEX in the champion's list, OQ1
 *      — injected at bundle-load, not authored on the def). `CHAMPION_SCHEMA.
 *      abilities` reshapes from `Ability[]` (inlined objects) to `AbilityId[]` (an
 *      ordered id-list referencing the registry). A content-schema reshape (old
 *      inlined-ability champions no longer parse) ⇒ PROTOCOL_VERSION 21→22. NOT a
 *      SIM_VERSION event: the loader resolves the id-list back into a concrete
 *      `Ability[]` (slot = index), so the sim sees the same resolved ability list
 *      and the per-tick STATE hash is untouched — only the CONTENT hash moves
 *      (champions serialize an id-list + a new abilities registry keyed by id).
 *  v23: BATCH phasing subsystem (#199) + server-side aura polarity (#168) — one
 *      sequential sim-lane PR. Both touch the aura wire/content schema, so they
 *      share ONE PROTOCOL bump.
 *        • #199 (phasing): a new `phasing` AURA primitive
 *          (`AURA_PRIM_PHASING_SCHEMA`, appended to `AURA_PRIMITIVE_SCHEMA`) with
 *          four independent pass-through toggles (projectiles/entities/pillars/
 *          walls, each `z.boolean().default(false)`). Additive to the primitive
 *          union, so existing aura defs parse byte-identically and the CONTENT
 *          hash is unchanged for non-users. It carries NO per-instance runtime
 *          state (a def-authored flag like #158 blocksLoS), so the per-entity
 *          STATE hash SHAPE is unchanged — but it IS an observable sim-behavior
 *          change (collision gating), so SIM_VERSION bumps too.
 *        • #168 (aura polarity): a REQUIRED `polarity` (`buff`|`debuff`,
 *          `AURA_POLARITY_SCHEMA`) on `AURA_DEF_SCHEMA` — no default (a real
 *          authoring decision, like `schools`/`slug`), so EVERY aura def gains the
 *          field (CONTENT hash changes; old unclassified defs no longer parse) —
 *          AND on `WIRE_AURA_SCHEMA` so the client reads buff/debuff off the wire
 *          instead of a local mirror table. NOT a per-entity STATE-hash change (an
 *          `AuraInstance` stores only the def id; polarity is looked up, not
 *          hashed), so #168 alone is NOT a SIM_VERSION event.
 *  v24: BATCH projectile arming distance (#207) + caster-to-ally self-relocation
 *      displace direction (#206) — one sequential sim-lane PR. Both are additive
 *      content-schema changes, so they share ONE PROTOCOL bump.
 *        • #207 (arming distance): a new optional `armAfterUnits` on
 *          `DELIVERY_PROJECTILE_SCHEMA` (`UNITS_SCHEMA.optional()`, absent ⇒ 0 ⇒
 *          armed from spawn). While the projectile has TRAVELLED less than this
 *          from its spawn origin it is fully INERT (collides with nothing,
 *          delivers no payload); once past it, it behaves normally. Additive, so
 *          existing projectile deliveries parse byte-identically and the CONTENT
 *          hash is unchanged for non-users. crossValidate rejects
 *          `armAfterUnits >= rangeUnits` (a projectile that could never arm).
 *          Distinct from the ability's `minRangeUnits` deadzone (#125). It IS an
 *          observable sim-behavior change AND a projectile hash-SHAPE change
 *          (`armAfterUnits` appended in `mixProjectile`), so SIM_VERSION bumps.
 *        • #206 (toTarget): a fifth `DISPLACE_DIRECTION_SCHEMA` literal
 *          `toTarget` — a SELF-relocation (moves the caster, like `behindTarget`)
 *          that lands the caster flush against the effect's TARGET on the
 *          caster's OWN APPROACH side (near side, radius-derived). `distance`
 *          MUST be ABSENT (same crossValidate rule as `behindTarget`). Additive
 *          enum value, so existing displace content parses byte-identically. It
 *          adds NO hashed ENTITY state (mutates the already-hashed caster x/y)
 *          and only appends an ordinal to the displace effect-payload encoding.
 *  v25: projectile-vs-projectile interceptor (#205) — a new optional
 *      `interceptsProjectiles { teams: enemies|all, burstRadius? }` block on
 *      `DELIVERY_PROJECTILE_SCHEMA` marking a projectile as an INTERCEPTOR that
 *      each tick destroys other in-flight projectiles it collides with (point =
 *      one-for-one; burst = detonate-in-radius). Additive + optional, so existing
 *      projectile deliveries parse byte-identically and the CONTENT hash is
 *      unchanged for non-users. DISTINCT from the body-blocking `interceptable`
 *      flag (no name collision). It IS an observable sim-behavior change AND a
 *      projectile hash-SHAPE change (`interceptsProjectiles` appended in
 *      `mixProjectile`), so SIM_VERSION bumps too (0.40.0). crossValidate rejects
 *      a non-positive `burstRadius`.
 *  v26: channeled abilities (#185) — a new optional `channel { durationTicks,
 *      tickIntervalTicks, fireOnStart, movementLocked }` block on `ABILITY_SCHEMA`
 *      (`CHANNEL_SCHEMA`) turning an ability into a CHANNEL: it fires its
 *      `effects`/`delivery` per-tick over a locked duration, holds the caster
 *      (movement gated by `movementLocked`), and ends on duration-elapse / player
 *      cancel / cast-blocking CC / interrupt / lock-on-target out-of-range.
 *      Additive + optional, so existing abilities parse byte-identically and the
 *      CONTENT hash is unchanged for non-users (same discipline as
 *      `recast`/`charges`). crossValidate makes `channel` MUTUALLY EXCLUSIVE with a
 *      non-zero `castTimeTicks` and with `offCast`/`castWhileMoving`/`recast`/`zone`
 *      delivery, and requires `tickIntervalTicks <= durationTicks`. Realizes the
 *      long-reserved `AIM_SNAPSHOT_SCHEMA.duringChannel` seam. It IS an observable
 *      sim-behavior change (a new `channeling` castState variant), so SIM_VERSION
 *      bumps too (0.41.0); no shipped/golden content channels, so the per-entity
 *      STATE-hash shape is unchanged for non-channelers and every golden per-tick
 *      hash is byte-identical (only the embedded simVersion moves).
 *  v27: live cursor re-aim mid-channel (#219) — a new optional
 *      `reaimPerFire: boolean` field on `CHANNEL_SCHEMA` (`z.boolean().default(false)`,
 *      so pre-#219 channel content parses byte-identically as `false`). When `true`
 *      on a SKILLSHOT (point-aim / `targetType: ground`) channel, each fire re-reads
 *      the caster's CURRENT cursor aim from the ORDINARY cast/cursor input (no new
 *      input kind) instead of the frozen start-of-channel point — Afflictor's
 *      steerable Destruction. The aim is SAMPLED ONLY on fire ticks (per the #185
 *      cadence — no continuous per-tick aim stream, no stored cursor), so it is a
 *      deterministic, replay-safe function of the recorded input log. crossValidate
 *      pins `reaimPerFire: true` to a skillshot channel (a lock-on channel re-reads
 *      its target, not the cursor). It IS an observable sim-behavior change
 *      (skillshot channels now fire at the moving aim), so SIM_VERSION bumps too
 *      (0.42.0) — but the re-aimed point is read transiently and never stored, so it
 *      adds NO hashed state and no shipped/golden content uses a re-aimed channel,
 *      so every golden per-tick STATE hash is byte-identical (only the embedded
 *      simVersion moves).
 *  v28: BATCH — independent per-instance DoT stacking (#218) + projectile
 *      ricochet/bounce (#44). Two additive content-schema changes bundled into one
 *      bump.
 *        #218: `STACKING_RULE_SCHEMA` gains an `independentInstances`
 *        member (`{ kind, maxInstances }`) — a new aura-stacking mode where every
 *        same-source application mints a fresh independent-timer instance, capped
 *        at `maxInstances` per (def, source) with oldest-first overflow eviction.
 *        Additive union member, so existing stacking content parses byte-identically
 *        and the mode adds NO hashed AuraInstance field.
 *        #44: `DELIVERY_PROJECTILE_SCHEMA` gains an optional `bounce { maxBounces,
 *        bounceOffChampions }` block (`BOUNCE_SCHEMA`) marking a projectile as a
 *        ricochet that reflects off pillars/arena-edge (and optionally struck
 *        champions), preserving its payload across bounces within the same travel
 *        budget. Additive + optional, so existing projectile deliveries parse
 *        byte-identically and the CONTENT hash is unchanged for non-users.
 *      Both are observable sim-behavior changes, so SIM_VERSION bumps too (0.43.0);
 *      no shipped/golden content uses either, so per-tick STATE hashes are
 *      byte-identical EXCEPT the `projectile-and-los` golden, whose live projectiles
 *      each gain a trailing bounce presence 0 (regenerated), and the hardcoded
 *      aura/projectile-free digest is UNCHANGED.
 *  v29: Chaos Void DECOMPOSED into composable zone primitives (#182). Three
 *      additive content-schema changes, all opt-in, so pre-#182 content parses
 *      byte-identically and the CONTENT hash is unchanged for non-users.
 *        • `AURA_PRIM_ABSORB_POOL_SCHEMA` gains `mode: shield|reservoir`
 *          (`z.union(...).default("shield")`). `shield` is the historical #103
 *          soak-incoming-damage pool; `reservoir` flips it into a FUND a
 *          `periodic` primitive on the same def draws down each firing (skipped
 *          by the shield drain). Defaulted, so existing shields are byte-identical.
 *        • `EFFECT_APPLY_AURA_SCHEMA` gains optional `absorbSeed` — override the
 *          attached instance's initial `absorbRemaining` with a runtime magnitude
 *          (the collapse bolt seeds its DoT reservoir from the well's accumulated
 *          pool). Optional, so existing applyAura parses byte-identically.
 *        • `DELIVERY_ZONE_SCHEMA` gains optional `absorb` (a projectile-sink
 *          modifier: team-selector + harvest strategy + an `absorbPool`
 *          accumulator) and `onExpire` (spawn a projectile carrying a
 *          reservoir-DoT when the zone ends — the dual of the projectile's
 *          `onExpire: ZONE_SPAWN`). Both optional, so ordinary zones parse
 *          byte-identically. There is NO fused flavor-named delivery/effect kind
 *          and NO bespoke DoT-budget field: absorb reuses `absorbPool`, collapse
 *          reuses the #183 recast router + a normal `periodic` DoT + the shared
 *          `spawnProjectile`.
 *      Observable sim-behavior change (projectile-absorbing zones + zone collapse
 *      + reservoir-funded periodics), so SIM_VERSION bumps too (0.44.0); no
 *      shipped/golden replay carries an absorbing zone, so per-tick STATE hashes
 *      are byte-identical (only the embedded simVersion moves) and the hardcoded
 *      zone-free digest is UNCHANGED.
 *  v30: seat→champion roster on `matchPhase` (#244). A new REQUIRED `roster`
 *      array (`WIRE_ROSTER_ENTRY_SCHEMA`: `{ seat, championId }`, one entry per
 *      joined seat) on `SERVER_MATCH_PHASE_SCHEMA`. Fixes the 3D character path
 *      (#237/#238) only ever resolving the LOCAL player's champion — the client
 *      otherwise has no way to learn another seat's championId (it's sent
 *      client→server only, on `createMatch`/`joinMatch`/`quickJoin`, and never
 *      echoed back). The server already tracks every seat's championId
 *      (`Room.playerList`); this mirrors it onto the wire so both clients can
 *      resolve EVERY seat, not just their own, when picking a 3D model.
 *      Presentation-only — no sim/hash involvement, so this is NOT a
 *      SIM_VERSION event. It IS a wire-shape change to a server→client message
 *      (`z.strictObject` rejects the new key on an old client's schema, and an
 *      old server never sends it to a new client's schema that requires it),
 *      hence the PROTOCOL_VERSION bump — caught at `hello` by the existing
 *      protocolVersion handshake before either side ever sees a mismatched
 *      `matchPhase`.
 *  v31: global speed-scale dial (#247). `TUNING_SCHEMA` gains an optional
 *      `championMoveSpeedScaleBp` and `projectileSpeedScaleBp` (basis-points multipliers, `z.number().default(10000)`)
 *      — a single knob `@pit/content`'s loader applies ONCE to every
 *      `speedUnitsPerTick`-shaped content value (champion `moveSpeed`, weapon/
 *      ability-projectile speed, zone-collapse-bolt speed) so re-pacing the game
 *      is a one-field edit, not a search-and-replace across every champion/
 *      weapon JSON file. Defaulted to `10000` (1x, a no-op), so pre-#247 content
 *      parses byte-identically and every shipped speed value is unchanged at
 *      load. Additive/optional schema field ⇒ PROTOCOL_VERSION bump (the
 *      convention bumps on every `TUNING_SCHEMA` addition regardless of
 *      backward-compat defaulting). NOT a SIM_VERSION event — no `@pit/sim`
 *      code changes; the scaling is a pure content-transform at load time, so
 *      the sim reads already-scaled numbers exactly as it reads hand-authored
 *      ones today.
 *  v32 (#252): WASD directional movement + strafing. Two new ADDITIVE
 *      `SimInput` kinds, appended after the existing arms:
 *        • `moveDir { forward, backward, strafeLeft, strafeRight }` — held-key
 *          state for facing-relative movement (replaces the entity's
 *          persisted movement intent, last-writer-wins, like `moveTo`).
 *        • `face { x, y }` — a continuous aim-lock point (right-click-hold in
 *          the default binding) that drives `Entity.facing` directly,
 *          independent of movement.
 *      `TUNING_SCHEMA` gains two defaulted basis-point fields:
 *      `strafeSpeedScaleBp` (default 10000 = 100% of forward speed) and
 *      `backwardSpeedScaleBp` (default 5000 = 50%) — both optional/defaulted,
 *      so pre-#252 content parses byte-identically. Existing `moveTo`/`stop`
 *      (click-to-move / follow-cursor) are UNCHANGED and remain selectable.
 *      SIM_VERSION bumps too (new hashed `Entity.moveIntent` state + momentum
 *      re-derivation) — see `packages/sim/src/version.ts`.
 *  v33 (#266): resource/points pools reach the wire.
 *      `WIRE_ENTITY_SCHEMA` gains `resource: number | null` and
 *      `points: number | null` (current-value-only, mirroring the sim's own
 *      `Entity.resource`/`Entity.points` `| null` shape). Both fields are
 *      REQUIRED on the wire object (this repo's convention is no
 *      `.optional()`, absence is an explicit `null`), so this is a
 *      wire-shape change: an old client's schema rejects the new keys and a
 *      new client's schema rejects a snapshot missing them — hence the
 *      PROTOCOL_VERSION bump, caught at `hello` before either side sees a
 *      mismatched `snapshot`. `max` stays OFF the wire (static content,
 *      resolved client-side from a small champion-keyed table, same pattern
 *      as `abilityLabels.ts`) — only `current` is per-tick state. NOT a
 *      SIM_VERSION event — no `@pit/sim` behavior changed; this is a pure
 *      wire-surface addition exposing state the sim already tracked.
 *  v34 (#257): team-conditional effect payload. `EFFECT_SCHEMA` gains a new
 *      `teamConditional` member (`{ kind, ifAlly, ifEnemy }`, each leg typed
 *      against a new `SIMPLE_EFFECT_SCHEMA` — every effect kind EXCLUDING
 *      `teamConditional`, so nesting one inside a leg is a schema parse
 *      failure, not a runtime check). Additive union member, so pre-#257
 *      content parses byte-identically. SIM_VERSION bumps too (a new effect
 *      kind resolves at `applyEffect`) — see `packages/sim/src/version.ts`.
 *  v35 (#276): countdown deadline reaches the wire. `SERVER_MATCH_PHASE_SCHEMA`
 *      gains a new REQUIRED `endsAtTick: Tick | null` field — the sim's own
 *      `PhaseCountdown.endsAtTick`, mirrored verbatim, non-null only when
 *      `phase === "countdown"` (the `winner` no-optionals convention above).
 *      Fixes the client fabricating its own countdown deadline (estimating
 *      `firstObservedTick + countdownTicks`, wrong on reconnect/late-join/
 *      spectate) by giving it the AUTHORITATIVE deadline instead. Required
 *      field on a `z.strictObject` ⇒ wire-shape change: an old client's
 *      schema rejects the new key and a new client's schema rejects a
 *      `matchPhase` missing it. Landed concurrently with #309 below — by the
 *      time this merged, PROTOCOL_VERSION had already become the auto-derived
 *      `${WIRE_EPOCH}:${SCHEMA_HASH}` composite, so this shape change is
 *      picked up by the SCHEMA_HASH regenerating automatically (no manual
 *      v35 bump needed under the new scheme; see `pnpm --filter @pit/protocol
 *      regen-schema-hash`). NOT a SIM_VERSION event — no `@pit/sim` behavior
 *      changed; this exposes state the sim already tracked and hashed.
 *
 * ─── #309: PROTOCOL_VERSION is no longer a hand-incremented integer ────────
 *
 * v1–v34 above chronicle the OLD scheme: a single bare integer, manually
 * bumped by whoever's PR touched a wire/content schema. Two PRs in flight
 * independently claiming "next number" collided twice in one session
 * (#257 vs #266, #269's goldens vs #257's) — a pure bookkeeping race with
 * zero actual semantic conflict. PROTOCOL_VERSION is now the composite
 * string `${WIRE_EPOCH}:${SCHEMA_HASH}`:
 *
 *   - `SCHEMA_HASH` (below) is AUTO-DERIVED — a truncated sha256 of every
 *     boundary schema's canonical JSON-Schema shape (`ClientMessage`/
 *     `ServerMessage`/`Replay`/`Champion`/`Ability`/`AuraDef`/`Map`/
 *     `Tuning` — see `schemaHash.ts`). It moves automatically, race-free,
 *     on every additive/shape-changing schema edit — no human bump, no
 *     coordination, no merge collision (two unrelated schema edits both
 *     move the hash to a value that's a pure function of HEAD, so whoever
 *     merges second just gets a different, still-correct, hash). It is a
 *     COMMITTED generated constant (`generated/schemaHash.generated.ts`),
 *     NOT computed at runtime — regenerate with
 *     `pnpm --filter @pit/protocol regen-schema-hash` (`--check` for the
 *     CI-safe dry run); `test/schemaHash.test.ts` fails loudly if it drifts.
 *   - `WIRE_EPOCH` (below) is the ONLY hand-bumped part of the version now,
 *     and ONLY for a genuinely BREAKING change that has NO schema-shape
 *     delta (JSON Schema can't express it) — e.g. reinterpreting an enum
 *     value's meaning, changing a numeric field's UNITS, tightening a
 *     `.refine()` predicate. Ordinary additive/shape-changing schema edits
 *     (the overwhelming majority of v1–v34's history) do NOT need a
 *     `WIRE_EPOCH` bump — the hash half already moves for those. Races on
 *     `WIRE_EPOCH` only happen when two PRs are BOTH making a genuinely
 *     breaking semantic change simultaneously — exactly the case that
 *     SHOULD force human coordination.
 *
 * The `hello`/`welcome`/`protocolMismatch` handshake is UNCHANGED in
 * behavior — still a strict equality check (never an ordering/"newer than"
 * comparison; verified across every consumer in this repo) — only the
 * VALUE'S shape changed from a bare integer to `${epoch}:${hash}`. Starting
 * point: `WIRE_EPOCH = 1` (a fresh counter for the new mechanism, not a
 * continuation of the old v1–v34 numbering).
 */
export const WIRE_EPOCH = 1;

/**
 * `PROTOCOL_VERSION` — see the `#309` block above. Equality-only; do not
 * add ordering/"newer than" comparisons anywhere this is consumed.
 */
export const PROTOCOL_VERSION = String(WIRE_EPOCH) + ":" + SCHEMA_HASH;

/**
 * Shape of `PROTOCOL_VERSION`: `<non-negative epoch int>:sha256:<hex>`. The
 * hex length (16) MUST match `SCHEMA_HASH_HEX_LEN` in `schemaHash.ts` —
 * duplicated here (not imported) to avoid a `limits.ts → schemaHash.ts →
 * content-schemas.ts → limits.ts` import cycle.
 */
export const PROTOCOL_VERSION_PATTERN = /^\d+:sha256:[0-9a-f]{16}$/u;
